import React, { useEffect, useRef, useState } from "react"
import api from "../../services/api"

import styles from './crm.module.css'
import Stage from "../../components/Crm/Stage"
import Select from "../../components/Form/Select"
import Button from './../../components/Form/Button'
import Input from "../../components/Form/Input"
import FormGroupInline from "../../components/Form/FormGroupInline"
import SideFilter from "./SideFilter"

import { useLocation, useNavigate, useParams } from "react-router-dom"
import { BiFilter } from "react-icons/bi"
import { FaPencilAlt } from "react-icons/fa"
import { HiPlus } from "react-icons/hi"
import { TfiReload } from "react-icons/tfi"
import { RiListSettingsLine } from "react-icons/ri"
import { toast } from "react-toastify"
import { useCrm } from "../../contexts/crmContext"
import { useData } from "../../contexts/dataContext"
import { useAuth } from "../../contexts/authContext"
import { areObjectsEqual, getItemForArrayById, getUsersBySelectedTeams, getValidUserIdsByTeams, showNotification } from "../../utils/utils"
import Loading from './../layouts/Loading/index'
import ModalNewLead from "../../components/Modal/ModalLead/ModalNewLead"
import ModalDeduplicatedLead from "../../components/Modal/ModalLead/ModalDeduplicatedLead"
import ModalManagementLead from "../../components/Modal/ModalManagementLead"
import { changeLastFunnel } from "../../schemas/Funnel"
import CircleLoading from "../../components/CircleLoading"
import { saveNewLead } from "../../schemas/Lead"
import { resolveRelativeLeadFilterDates, serializeLeadFilters } from "../../utils/leadFilters"
import { getSavedLeadFilters } from "../../schemas/SavedLeadFilter"
import ActiveFilterChips, { buildLeadFilterChips } from "../../components/LeadFilters/ActiveFilterChips"

const Crm = () => {
    const { account_id, funnel_id } = useParams()
    const {
        funnels, teams, users, products, origins, campaigns, flagsByoptions, typeDate, status,
        metaAdsAdsName, metaAdsAdsetsName, metaAdsCampaignsName, metaAdsFormsName, metaAdsPagesName,
        loadCampaignsByOrigins, updateLastFunnel,
    } = useData()
    const { stages, changeStages, loadingStages, leads, setLeads, currentLeads, totalLeads, setTotalLeads, filters, setFilters, initialFilters, setInitialFilters, setFilteredDataLead, handleUpdateLead, reload, setReload, updateLeadById, verifyFiltersByLead, removeLeadById, permissionIsOk, modalNewLead, closeModalNewLead, newLead, setNewLead, openModalLead, openModalFunnel, modalManagement, openLeadsManagement, closeModalLeadsManagement } = useCrm()
    const [funnel, setFunnel] = useState(funnel_id)
    const [filtersTemp, setFiltersTemp] = useState(filters)
    const [changedFilters, setChangedFilters] = useState(false)
    const [sideFilterActive, setSideFilterActive] = useState(false)
    const [loading, setLoading] = useState(false)
    const [deduplicatedLead, setDeduplicatedLead] = useState(null)
    const defaultFilterLoaded = useRef("")
    const leadRequestId = useRef(0)

    const { permissions, user } = useAuth()

    const location = useLocation()
    const navigate = useNavigate()

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search)
        const openModal = queryParams.get('openModal')
        const leadId = queryParams.get('leadId')
        if (openModal === '1' && leadId) {
            openModalLead(leadId)
            queryParams.delete('openModal')
            queryParams.delete('leadId')
            setTimeout(() => {
                navigate({ pathname: location.pathname, search: queryParams.toString() }, { replace: true })
            }, 500)
        }
    }, [location.search])

    useEffect(() => {
        if (account_id && funnel_id) {
            setFunnel(funnel_id)
        }
    }, [account_id, funnel_id])

    useEffect(() => {
        if (account_id && funnel) {
            getInitialLeads()
            navigate(`/${account_id}/kanban/${funnel}`)
            changeLastFunnel(funnel).then(funnel_id => updateLastFunnel(funnel_id))
        }
    }, [account_id, funnel])

    useEffect(() => {
        if (permissionIsOk && funnel) {
            updateStages(funnel)
        }
    }, [filters, funnel, permissionIsOk])

    useEffect(() => {
        setInitialValuesByFilter("teams", teams?.map(team => { team.selected = true; return team }))
    }, [teams])

    useEffect(() => {
        setInitialValuesByFilter("users", users?.map(user => { user.selected = true; return user }))
    }, [users])

    useEffect(() => {
        const defaultFilterKey = `${account_id || ""}:${user?._id || ""}`
        if (defaultFilterLoaded.current === defaultFilterKey || !account_id || !user?._id || !funnel || !funnels?.length || !teams?.length || !users?.length) return
        defaultFilterLoaded.current = defaultFilterKey

        getSavedLeadFilters("kanban").then(savedFilters => {
            const defaultFilter = savedFilters.find(item => item.is_default)
            if (defaultFilter?.filters) {
                const resolvedFilters = resolveRelativeLeadFilterDates(defaultFilter.filters)
                setFiltersTemp(resolvedFilters)
                getLeadsByFunnelAndFilter(true, resolvedFilters)
            }
        }).catch(() => { })
    }, [account_id, user?._id, funnel, funnels, teams, users])

    useEffect(() => {
        handleFiltersChanged()
    }, [filtersTemp])

    useEffect(() => {
        loadCampaignsByOrigins(filtersTemp.origins)
    }, [filtersTemp.origins])

    const setInitialValuesByFilter = (name, array) => {
        if (array?.length > 0) {
            const newValue = array?.filter(el => el.selected)?.map(el => { return el.id })
            setInitialFilters(previousValue => {
                return {
                    ...previousValue,
                    [name]: newValue
                }
            })
            setFilters(previousValue => {
                return {
                    ...previousValue,
                    [name]: newValue
                }
            })
            setFiltersTemp(previousValue => {
                return {
                    ...previousValue,
                    [name]: newValue
                }
            })
        }
    }

    const getInitialLeads = async () => {
        if (!loading) {
            getLeadsByFunnelAndFilter()
        }
    }

    const getLeadsByFunnelAndFilter = (updateFilters = false, filterValues = filtersTemp) => {
        const requestId = ++leadRequestId.current
        const resolvedFilterValues = resolveRelativeLeadFilterDates(filterValues)
        setLoading(true)
        const queryString = serializeLeadFilters(resolvedFilterValues)
        api.get(`/leads?funnel_id=${funnel}&${queryString}`).then(async response => {
            if (requestId !== leadRequestId.current) return
            const newLeads = response.data?.data?.leads || []
            const totalLeads = response.data?.data?.totalLeads || 0
            setLeads(newLeads)
            if (updateFilters) {
                setFilters(resolvedFilterValues)
                setFiltersTemp(resolvedFilterValues)
            }
            setTotalLeads(totalLeads)
            setChangedFilters(false)
            setFilteredDataLead(previousValue => ++previousValue)
            closeSideFilter()
            setReload(false)
            setLoading(false)
        }).catch(err => {
            if (requestId !== leadRequestId.current) return
            toast.error(err?.response?.data?.message)
            setLoading(false)
        })
    }

    const handleChangeFilters = (name, value) => {
        setFiltersTemp(previousValue => {
            const operatorKey = ["users", "products", "flags"].includes(name) ? `${name}Operator` : undefined
            const resetPresenceOperator = operatorKey && value?.length > 0 && ["is_empty", "is_not_empty"].includes(previousValue[operatorKey])
            return {
                ...previousValue,
                [name]: value,
                ...(resetPresenceOperator ? { [operatorKey]: "any" } : {}),
                ...(name === "teams" ? { users: getValidUserIdsByTeams(previousValue.users, value, users, teams, [], previousValue.teams) } : {}),
            }
        })
    }

    const handleFiltersChanged = () => {
        setChangedFilters(!areObjectsEqual(filters, filtersTemp))
    }

    const cleanFilters = () => {
        setFiltersTemp(initialFilters)
    }

    const activeFilterChips = buildLeadFilterChips({
        filters,
        options: {
            products, origins, campaigns, flags: flagsByoptions, typeDate, status, teams, users,
            metaAdsAdsName, metaAdsAdsetsName, metaAdsCampaignsName, metaAdsFormsName, metaAdsPagesName,
        },
    })

    const openSideFilter = () => {
        setSideFilterActive(true)
    }

    const closeSideFilter = () => {
        setSideFilterActive(false)
    }

    const updateStages = (funnel_id) => {
        const funnelIsValid = getItemForArrayById(funnels, "_id", funnel_id)
        if (funnelIsValid) {
            changeStages(funnel_id)
        } else if (funnels?.length && funnels?.[0]?._id) {
            return navigate(`/${account_id}/kanban/${funnels[0]._id}`)
        }
    }

    const handleSaveNewLead = (e, open = false) => {
        e.preventDefault()
        setLoading(true)
        saveNewLead(newLead).then(data => {
            if (data) {
                if (!data?.deduplicated) {
                    addNewLead(data)
                } else {
                    setDeduplicatedLead(data)
                }
                if (open && !data?.deduplicated) {
                    openModalLead(data._id)
                }
            }
            changeStages(funnel)
            if (!data?.deduplicated) {
                closeModalNewLead()
            }
        }).catch(err => { }).finally(() => setLoading(false))
    }

    const addNewLead = (newLead) => {
        setLeads(previousValue => {
            const leadAlreadyExists = previousValue?.some(lead => lead._id === newLead._id)
            const filtersOk = verifyFiltersByLead(newLead)

            if (leadAlreadyExists) {
                if (filtersOk) {
                    updateLeadById(newLead, newLead._id)
                } else {
                    removeLeadById(newLead._id)
                }
            } else {
                showNotification("Um novo lead foi cadastrado!", "Entre em contato o mais breve possível, para que ele não esfrie!")
            }

            return !leadAlreadyExists && filtersOk ? [newLead, ...previousValue] : previousValue
        })
    }

    // Ajustes
    const onChangeStage = (card, overCardId, oldStageId, stage, update = true) => {
        setLeads(previousValue => {
            const newValue = previousValue?.map((lead) => {
                if (lead._id === card._id) {
                    lead.stage_id = update ? stage._id : oldStageId
                }
                return lead
            })

            if (update) {
                handleUpdateLead({ "stage_id": stage._id, funnel_id: funnel }, card._id).catch(() => onChangeStage(card, overCardId, oldStageId, stage, false))
            }

            // Reordenar os cards
            // Criar ordenação no Stage
            // Ordena pela próxima tarefa
            // Se não houver uma tarefa agendada pela data de entrada
            if (overCardId) {
                const index = previousValue.findIndex((lead) => {
                    return lead._id === overCardId
                })
                if (index >= 0) {
                    // previousValue.splice(index, 0, lead)
                }
            }

            return newValue
        })
    }

    return (
        <>
            <div className={styles.filters}>
                <div className={styles.groupLeft}>
                    <div className={styles.funnelSelect}>
                        <Select name="Funil" placeholder="Pesquise um funil" options={funnels || []} selected={funnel} onChange={setFunnel} multiple={false} />
                    </div>
                    {permissions?.edit_funnels > 0 &&
                        <div className={styles.funnelActions}>
                            <Button variant="accent" className="action" onClick={() => openModalFunnel(true)} title="Criar funil" aria-label="Criar funil">
                                <HiPlus />
                            </Button>
                            <Button variant="secondary" className="action" onClick={() => openModalFunnel(false, funnel)} title="Editar funil" aria-label="Editar funil">
                                <FaPencilAlt />
                            </Button>
                        </div>
                    }
                </div>
                <div className={styles.groupCenter}>
                    <Select name="Equipe" placeholder="Pesquise uma equipe" options={teams || []} selected={filtersTemp.teams || []} onChange={(value) => handleChangeFilters("teams", value)} multiple={true} />
                    {permissions?.crm > 1 &&
                        <Select name="Responsável" placeholder="Pesquise um responsável" options={getUsersBySelectedTeams(filtersTemp?.teams, users, teams)} selected={filtersTemp.users || []} onChange={(value) => handleChangeFilters("users", value)} multiple={true} />
                    }
                </div>
                <div className={styles.groupRight}>
                    {reload &&
                        <div className={styles.reload} onClick={() => getLeadsByFunnelAndFilter(true)}>
                            <TfiReload />
                        </div>
                    }
                    <div className={styles.numberLeads}>
                        <span>{currentLeads} / {totalLeads}</span>
                    </div>
                    <Input
                        type="search"
                        placeholder="Buscar"
                        theme="light"
                        value={filtersTemp.search || ""}
                        onChange={(e) => handleChangeFilters("search", e.target.value)}
                    />
                    {permissions?.settings > 0 &&
                        <div className={styles.settings} onClick={() => openLeadsManagement()}>
                            <RiListSettingsLine className="orange" />
                        </div>
                    }
                    <div className={styles.rightFilterAction} onClick={openSideFilter}>
                        <BiFilter />
                    </div>
                </div>
            </div>
            <div className={styles.activeFiltersRow}>
                <ActiveFilterChips items={activeFilterChips} onOpen={openSideFilter} />
            </div>
            <div className={styles.funnel}>
                {changedFilters &&
                    <div className={styles.changedFilters}>
                        <FormGroupInline>
                            <Button variant='secondary' type="submit" onClick={() => getLeadsByFunnelAndFilter(true)}>Aplicar Filtros</Button>
                            <Button variant='primary' type="submit" onClick={() => setFiltersTemp(filters)}>Limpar</Button>
                        </FormGroupInline>
                    </div>
                }
                <div className={`${styles.stages} ${loadingStages ? styles.loading : ''}`}>
                    {loadingStages && <CircleLoading />}
                    {!loadingStages && stages?.length > 0 && stages.map(stage => (
                        <Stage key={stage._id} id={stage._id} stage={stage} leads={leads?.filter(lead => lead.stage_id === stage._id)} onChangeStage={onChangeStage} />
                    ))}
                </div>
            </div>
            {sideFilterActive &&
                <SideFilter
                    filters={filtersTemp}
                    handleChange={handleChangeFilters}
                    onLoadFilters={filterValues => setFiltersTemp({ ...filterValues, users: getValidUserIdsByTeams(filterValues.users, filterValues.teams, users, teams) })}
                    onSubmit={() => getLeadsByFunnelAndFilter(true)}
                    onClose={closeSideFilter}
                    cleanFilters={cleanFilters}
                />
            }
            {modalNewLead &&
                <ModalNewLead
                    value={newLead}
                    onChange={setNewLead}
                    onSubmit={handleSaveNewLead}
                    listStages={stages?.map(stage => { return { id: stage._id, value: stage.name } })}
                    listProducts={products?.map(product => { return { id: product._id, value: product.name } })}
                    closeModal={closeModalNewLead}
                />
            }
            <ModalDeduplicatedLead
                lead={deduplicatedLead}
                onClose={() => setDeduplicatedLead(null)}
                onOpenLead={openModalLead}
                onFinish={() => {
                    setDeduplicatedLead(null)
                    closeModalNewLead()
                }}
            />
            {permissions?.settings > 0 && modalManagement &&
                <ModalManagementLead
                    closeModal={closeModalLeadsManagement}
                />
            }
            {loading && <Loading fullPage={true} />}
        </>
    )
}

export default Crm
